import {
    ArrayUtil, Collection, DOMUtil, DynamicLoader, getThemeForKey, HeightWidth, LogManager, MixinUtil, ModelRow,
    StringUtil, ValueDisplayValue
} from "@mcleod/core";
import {
    BlurEvent, ComponentConstructor, DataSourceMode, DesignerInterface, Panel, PrintableEvent, ValidationResult
} from "../..";
import { CaptionLabel } from "../label/CaptionLabel";
import { CaptionedMixin } from "../../mixins/CaptionedMixin";
import { CaptionedMixinInterface } from "../../mixins/CaptionedMixinInterface";
import { ComponentPropDefinition, ComponentPropDefinitions } from "../../base/ComponentProps";
import { DesignableObjectTempState } from "../../base/DesignableObjectTempState";
import { Event } from "../../events/Event";
import { getCurrentDataSourceMode, getRelevantModelRow } from "../../base/ComponentDataLink";
import { LabelProps } from "../label/LabelProps";
import { ListenerListDef } from "../../base/ListenerListDef";
import { PrintableLabel } from "../label/PrintableLabel";
import { PrintableMixin } from "../../mixins/PrintableMixin";
import { PrintableMixinInterface } from "../../mixins/PrintableMixinInterface";
import { RadioGroupPropDefinitions, RadioGroupProps } from "./RadioGroupProps";
import { RadioOption } from "./RadioOption";
import { RadioOptionSelectionEvent, RadioOptionSelectionListener } from "../../events/RadioOptionSelectionEvent";
import { TooltipSource } from "../../page/tooltips/TooltipSource";
import { TooltipSourceType } from "../../page/tooltips/TooltipSourceType";
import { ValidationResultType } from "../../base/ValidationResult";

const log = LogManager.getLogger("components.radio.RadioGroup");

export abstract class RadioGroup extends Panel implements RadioGroupProps {
    private static readonly afterSelectionListenerDef: ListenerListDef = { listName: "_afterSelectionListener" };
    private static readonly beforeSelectionListenerDef: ListenerListDef = { listName: "_beforeSelectionListener" };
    private _allowIndeterminate: boolean = false;
    private _options: RadioOption[];
    private _captionVisible: boolean;
    private _captionVisibleInsideTable: boolean;
    private _defaultDataValue: boolean;
    private _focusable: boolean;
    private panelOptions: Panel;
    private prePrintableState: DesignableObjectTempState;
    private printableLabel: PrintableLabel;
    private _selectedOption: RadioOption;
    private _validationWarning: string;
    private validationWarningProps: Partial<RadioGroupProps>;
    
    constructor(props?: Partial<RadioGroupProps>) {
        super(props, false);
        // The radio group itself is not focusable, but the options it contains are.
        this._element.setAttribute("tabindex", "-1");
        this.captionLabel = new CaptionLabel(this);
        this.captionVisible = true;
        this.captionVisibleInsideTable = false;
        this.addOptionsPanel();
        this.setProps({ themeKey: "radioGroup.border", ...props });
        this.supportsEditSecurity = true;
        this.addMountListener(() => this.syncCaption());
    }

    override syncInnerComponentIds() {
        this.setInnerComponentId(this.captionLabel, "captionLabel");
        this.setInnerComponentId(this.panelOptions, "panelOptions");
        this.printableLabel?.syncId;
        this.syncOptionIds();
    }

    private syncOptionIds() {
        this.options?.forEach(option => this.setOptionId(option));
    }

    private setOptionId(option: RadioOption) {
        this.setInnerComponentId(option, `${option.shortName}-${option.value}`);
    }

    private addOptionsPanel() {
        this.panelOptions = Panel.createNoMarginNoPaddingPanel({ _designer: this._designer });
        this.panelOptions.alternateDesignerTarget = this;
        this.add(this.panelOptions);
    }

    get options(): RadioOption[] {
        return this._options;
    }

    set options(value: RadioOption[]) {
        this.options?.forEach(option => this.panelOptions.remove(option));
        this._options = [];
        this.addOptions(...value);
    }

    abstract get optionClass(): ComponentConstructor;
    abstract resolveOption(value: ValueDisplayValue | RadioOption): RadioOption;
    abstract resolveOptions(value: ValueDisplayValue[] | (() => ValueDisplayValue[]) | RadioOption[] |
        (() => RadioOption[])): RadioOption[];

    // We provide separate getters/setters for 'items' and 'options', mainly to make it easier for the designer (which
    // deals in 'items', as it needs to display/serialize the ValueDisplayValue[]).  In particular, this getter for
    // items is very helpful.
    get items(): ValueDisplayValue[] {
        return this.options?.map(option => option.valueDisplayValue);
    }

    set items(value: ValueDisplayValue[] | (() => ValueDisplayValue[])) {
        const newOptions = this.resolveOptions(value);
        this.options?.forEach(option => this.panelOptions.remove(option));
        this._options = [];
        this.addOptions(...newOptions);
    }

    public addOptions(...value: (ValueDisplayValue|RadioOption)[]) {
        if (this.options == null)
            this._options = [];
        value?.forEach(optionSource => {
            const resolvedOption = this.resolveOption(optionSource);
            resolvedOption.group = this;
            this.setOptionId(resolvedOption);
            this.options.push(resolvedOption);
            this.panelOptions.add(resolvedOption);
        });
        // Call syncEnabled() here so that each option's enabled value will be evaluated.  Doing this here, after all of
        // the options are added to the group, ensures that each option knows how many options are present in the group.
        // That impacts if each individual option is enabled/focusable.
        this._syncEnabled();
    }

    get allowIndeterminate(): boolean {
        return this._allowIndeterminate;
    }

    set allowIndeterminate(value: boolean) {
        this._allowIndeterminate = value;
    }

    get selectedOption(): RadioOption {
        return this._selectedOption;
    }

    set selectedOption(value: RadioOption) {
        this.internalSetSelected(value);
    }

    setSelectedOption(value: RadioOption, originatingEvent: Event) {
        this.internalSetSelected(value, originatingEvent);
    }

    internalSetSelected(value: RadioOption, originatingEvent?: Event) {
        if (value === this.selectedOption || (value == null && this.selectedOption == null))
            return;
        const oldSelection = this.selectedOption;
        const beforeSelectionEvent = this.createSelectionEvent(oldSelection, value, originatingEvent);
        this.fireBeforeSelectionListeners(beforeSelectionEvent);
        if (beforeSelectionEvent.defaultPrevented)
            return;
        if (this.selectedOption != null)
            this.selectedOption.selected = false;
        this._selectedOption = value;
        if (this.selectedOption != null)
            this.selectedOption.selected = true;
        this.syncPrintableLabelText();
        this.validate();
        this.internalUpdateBoundData();
        this.storeUserChoiceIfRemembered();
        const afterSelectionEvent = this.createSelectionEvent(oldSelection, this.selectedOption, originatingEvent);
        this.fireAfterSelectionListeners(afterSelectionEvent);
    }

    public get selectedValue(): any {
        return this.selectedOption?.value;
    }

    public setSelectedFromValue(value: any) {
        const optionsWithValue: RadioOption[] = this.options?.filter(option => option.value === value);
        this.selectedOption = ArrayUtil.getFirstElement(optionsWithValue);
    }

    public get selectedDisplayValue(): string {
        return this.selectedOption?.displayValue;
    }

    public setSelectedFromDisplayValue(value: any) {
        const optionsWithValue: RadioOption[] = this.options?.filter(option => option.displayValue === value);
        this.selectedOption = ArrayUtil.getFirstElement(optionsWithValue);
    }

    get valueAsString(): string {
        return this.selectedOption?.valueAsString;
    }

    set valueAsString(value: string) {
        this.setSelectedFromValue(value);
    }

    private internalUpdateBoundData() {
        const row = getRelevantModelRow(this);
        const mode = getCurrentDataSourceMode(this);
        this.updateBoundData(row, mode);
    }

    override updateBoundData(row: ModelRow, mode: DataSourceMode) {
        if (this.field != null)
            row?.set(this.field, this.selectedValue, this);
    }

    override get defaultDataValue(): boolean {
        return this._defaultDataValue;
    }

    public set defaultDataValue(value: boolean) {
        this._defaultDataValue = value;
    }

    public override applyDefaultDataValue() {
        if (this.defaultDataValue)
            this.setSelectedFromDisplayValue(this.defaultDataValue);
    }

    override isEmpty(): boolean {
        return this.selectedOption == null;
    }

    evaluateOptionBlur(event: BlurEvent) {
        const relatedTarget = event.domEvent?.relatedTarget;
        if (DOMUtil.isOrContains(this._element, relatedTarget as Element) !== true)
            this.validate();
    }

    override validate(checkRequired: boolean = true, showErrors: boolean = true): ValidationResult[] {
        if (this.printable)
            return null;
        const result: ValidationResult[] = [];
        if (checkRequired) {
            const isRequiredAndEmpty = this._designer == null && this.required && this.isEmpty();
            const validationMessage = showErrors && isRequiredAndEmpty ? "Required" : null;
            this.validationWarning = validationMessage;
            result.push({
                component: this,
                isValid: !isRequiredAndEmpty,
                validationMessage,
                type: ValidationResultType.REQUIRED
            });
        }
        return result;
    }

    override resetValidation() {
        this.validationWarning = null;
    }

    get validationWarning(): string {
        return this._validationWarning;
    }

    set validationWarning(value: string) {
        if (this._validationWarning === value)
            return;
        this._validationWarning = value;
        this.syncValidationWarningProps();
        this.syncTooltipMouseListeners();
    }

    private syncValidationWarningProps() {
        if (this.validationWarningProps != null) {
            this.revertTempState();
            this.validationWarningProps = null;
        }

        if (!StringUtil.isEmptyString(this._validationWarning)) {
            this.validationWarningProps = {
                borderColor: getThemeForKey("radioGroup.borderErrorColor"),
            };
            this.applyTempState(this.validationWarningProps);
        }
    }

    public override get tooltipSources(): TooltipSource[] {
        const result = super.tooltipSources;
        if (this._designer == null) {
            result.push(this.validationWarningTooltipSource);
        }
        return result;
    }

    private get validationWarningTooltipSource(): TooltipSource {
        return new TooltipSource(TooltipSourceType.VALIDATION_WARNING, this.validationWarning);
    }

    override focus() {
        if (this.focusable)
            this.getFocusTarget()?.focus();
    }

    override getFocusTarget(): HTMLElement {
        return this.findFocusableComponent()?.getFocusTarget();
    }

    findFocusableComponent(): RadioOption {
        // Put focus on selected radio option, if one is selected.
        // Otherwise, put focus on the first radio option.
        // If the radio group has no options, it should not be focusable anyway.
        if (this.selectedOption != null)
            return this.selectedOption;
        return ArrayUtil.getFirstElement(this.options);
    }

    get focusable(): boolean {
        return (this._focusable ?? this.getPropertyDefinitions().focusable.defaultValue) &&
            this.getFocusTarget() != null;
    }

    set focusable(value: boolean) {
        this._focusable = value;
        this._syncEnabled();
    }

    override displayComponentData(data: ModelRow, allData: ModelRow[], rowIndex: number) {
        const value = (data != null && this.field != null) ? data.get(this.field) : null;
        this.setSelectedFromValue(value);
    }

    get caption(): string {
        return this.super_get_caption();
    }

    set caption(value: string) {
        if (this.captionValueMatches(value) === true) {
            return;
        }
        const oldValue = this.caption;
        this.super_set_caption(value);
        this.syncCaption();
        this._matchIdToValue(oldValue, value);
    }

    get captionLabelProps(): Partial<LabelProps> {
        return {
            ...this.super_get_captionLabelProps(),
            height: 16,
            paddingBottom: 2
        };
    }

    get captionVisible(): boolean {
        return this._captionVisible;
    }

    set captionVisible(value: boolean) {
        if (value === this.captionVisible)
            return;
        this._captionVisible = value;
        this.syncCaptionDisplay();
    }

    get captionVisibleInsideTable(): boolean {
        return this._captionVisibleInsideTable;
    }

    set captionVisibleInsideTable(value: boolean) {
        this._captionVisibleInsideTable = value;
        this.syncCaption();
    }

    syncCaption() {
        if (this.captionLabel != null) {
            this.captionLabel.text = this.getPrefixedCaption();
            this.syncCaptionDisplay();
        }
    }

    syncCaptionDisplay() {
        const captionIsVisible = this.captionVisible &&
            !StringUtil.isEmptyString(this.caption) &&
            (this.insideTableCell !== true || this.captionVisibleInsideTable === true)
        const captionLabelInDOM = this.contains(this.captionLabel);
        if (captionIsVisible) {
            if (!captionLabelInDOM)
                this.insert(this.captionLabel, 0);
        }
        else if (captionLabelInDOM)
            this.remove(this.captionLabel);

    }

    override _applyEnabled(value: boolean): void {
        this.options?.forEach(option => option.enabled = value);
    }

    override dataSourceModeChanged(mode: DataSourceMode) {
        super.dataSourceModeChanged(mode);
        if (mode === DataSourceMode.SEARCH) {
            this.allowIndeterminate = true;
            this.selectedOption = undefined;
        }
        else
            this.allowIndeterminate = false;
        this.syncPrintable();
    }

    protected _applyPrintable(value: boolean) {
        // Hide options and display printable label displaying current selection
        if (value === true && this.printableLabel == null) {
            this.prePrintableState = new DesignableObjectTempState({ height: this.height, width: this.width });
            let heightWidth: HeightWidth;
            this.doWhileDimensionsComputed(() => heightWidth = DOMUtil.getElementHeightWidth(this._element));
            this.height = this._designer == null ? heightWidth.height : null;
            this.width = this._designer == null ? heightWidth.width : null;
            // Cannot instantiate using class directly, causes circular reference
            const printableLabelClass = DynamicLoader.getClassForPath("components/components/label/PrintableLabel");
            this.printableLabel = new printableLabelClass(this, {
                paddingLeft: 0,
                fillRow: true
             });
            this.panelOptions.visible = false;
            this.add(this.printableLabel);
            this.syncPrintableLabelText();
        }
        else if (value !== true && this.printableLabel != null) {
            this.remove(this.printableLabel);
            this.printableLabel = null;
            this.setProps(this.prePrintableState?.originalProps);
            this.prePrintableState = null;
            this.panelOptions.visible = true;
        }
        else {
            log.debug(this, "Radio group already configured for printable setting of %o", this.printable);
            return;
        }
        this.firePrintableListeners(new PrintableEvent(this, this.printableLabel));
    }

    private syncPrintableLabelText() {
        if (this.printableLabel != null) {
            this.printableLabel.text = (this._designer == null ? this.selectedOption?.displayValue : this.field) ||
                "No selection";
        }
    }

    abstract createSelectionEvent(oldSelection: RadioOption, newSelection: RadioOption, originatingEvent?: Event):
        RadioOptionSelectionEvent;

    public addBeforeSelectionListener(value: RadioOptionSelectionListener): RadioGroup {
        return this.addEventListener(RadioGroup.beforeSelectionListenerDef, value) as RadioGroup;
    }

    public removeBeforeSelectionListener(value: RadioOptionSelectionListener): RadioGroup {
        return this.removeEventListener(RadioGroup.beforeSelectionListenerDef, value) as RadioGroup;
    }

    private fireBeforeSelectionListeners(event: RadioOptionSelectionEvent) {
        this.fireListeners(RadioGroup.beforeSelectionListenerDef, event);
    }

    public addAfterSelectionListener(value: RadioOptionSelectionListener): RadioGroup {
        return this.addEventListener(RadioGroup.afterSelectionListenerDef, value) as RadioGroup;
    }

    public removeAfterSelectionListener(value: RadioOptionSelectionListener): RadioGroup {
        return this.removeEventListener(RadioGroup.afterSelectionListenerDef, value) as RadioGroup;
    }

    private fireAfterSelectionListeners(event: RadioOptionSelectionEvent) {
        this.fireListeners(RadioGroup.afterSelectionListenerDef, event);
    }

    override getListenerDefs(): Collection<ListenerListDef> {
        return {
            ...super.getListenerDefs(),
            "afterSelection": { ...RadioGroup.afterSelectionListenerDef },
            "beforeSelection": { ...RadioGroup.beforeSelectionListenerDef },
            ...MixinUtil.getMixinListenerDefs(this)
        };
    }

    override getPropertyDefinitions(): ComponentPropDefinitions {
        return RadioGroupPropDefinitions.getDefinitions();
    }

    override getSearchValues(): string[] {
        return [ this.selectedOption?.valueAsString ?? null ];
    }

    override getBasicValue(): any {
        return this.selectedValue;
    }

    /**
     * Override to avoid serializing the inner radio options and the caption label
     */
    override _serializeNonProps(): string {
        return "";
    }

    protected override determinePropertyDefaultValue(prop: ComponentPropDefinition): any {
        if (prop.name === "focusable") {
            if (this._focusable == null && ArrayUtil.isEmptyArray(this.options) === true)
                return false;
        }
        else if (prop.name === "borderWidth")
            return getThemeForKey("radioGroup.border")?.borderWidth;
        else if (prop.name === "borderColor")
            return getThemeForKey("radioGroup.border")?.borderColor;
        else if (prop.name === "items") {
            // Recreate items from bound Value/Display Values in the metadata, and test them against the items in use.
            // If they are the same, return this.items as the default value so that they are not included in the
            // serialized version of the component.
            const items = this.items;
            if (Array.isArray(items) && this._boundField?.dbDisplayValues != null) {
                const boundDbDisplayValues = this._boundField?.dbDisplayValues;
                if (boundDbDisplayValues != null &&
                    items != null &&
                    boundDbDisplayValues.length === items.length) {
                    const matches = ValueDisplayValue.findMatches(items, boundDbDisplayValues);
                    if (matches?.length === boundDbDisplayValues.length)
                        return items;
                }
            }
        }
        return super.determinePropertyDefaultValue(prop);
    }

    override get _designer(): DesignerInterface {
        return super._designer;
    }

    override set _designer(value: DesignerInterface) {
        super._designer = value;
        // Handle when the designer is being set after inner components are created
        if (this.captionLabel != null) {
            this.captionLabel._designer = value;
            this.captionLabel.alternateDesignerTarget = this;
        }
        this.options?.forEach(option => {
            option._designer = value;
            option.alternateDesignerTarget = this;
        });
    }

    protected override _initialDropInDesigner(): void {
        this.addOptions(new ValueDisplayValue("1", "Option 1"), new ValueDisplayValue("2", "Option 2"));
    }

    protected override _fieldBindingChanged(): void {
        super._fieldBindingChanged();
        this.createOptionsFromDbDisplayValues();
    }

    /**
     * Create radio option from Value/Display Values that are present in the metadata.
     */
    private createOptionsFromDbDisplayValues() {
        if (this._boundField?.dbDisplayValues != null)
            this.items = this._boundField.dbDisplayValues;
    }
}
// Apply mixins at runtime
MixinUtil.applyMixins(RadioGroup, [ CaptionedMixin, PrintableMixin ]);

// TypeScript interface declaration for mixin integration
export interface RadioGroup extends CaptionedMixinInterface, PrintableMixinInterface {
}
